import './ManageMarketplace.scss';

import { getMarketplace } from 'APIs/marketplaces/marketplaces';
import { Marketplace } from 'APIs/marketplaces/marketplaces.types';
import Message from 'Components/Message';
import Spinner from 'Components/Spinner/Spinner';
import TabContent from 'Components/Tabs/TabContent';
import Tabs from 'Components/Tabs/Tabs';
import { baseUri } from 'Constants/Constants';
import { MouseEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import getErrorMessage from 'utils/getErrorMessage';

import MarketplaceBasicInfo from './Components/MarketplaceBasicInfo';
import MarketplaceGeneralSettings from './Components/MarketplaceGeneralSettings';
import MarketplaceUsers from './Components/MarketplaceUsers';
import MerchantMarketplaceSettings from './Components/MerchantMarketplaceSettings';
import MerchantList from './Components/Merchants/MerchantList';
import { CreateMarketplaceContext } from './context';
import { LpBox, NavBackLink, SpinnerWrapper, Wrapper } from './styles';

const ManageMarketplace = (): ReactElement => {
  const { apiBaseUri, apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const { marketplaceId, manage } = useParams<{ marketplaceId: string; manage: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [marketplace, setMarketplace] = useState<Marketplace | null>(null);

  const handleCloseMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setErrorMsg('');
  };

  const fetchMarketplaceDetails = useCallback(async () => {
    if (!apiBaseUri || !marketplaceId) {
      return;
    }
    try {
      if (!marketplace) setIsLoading(true);
      const marketplaceResp = await getMarketplace(apiBaseUri, marketplaceId);
      setMarketplace(marketplaceResp);
    } catch (e) {
      setErrorMsg(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUri, marketplace, marketplaceId]);

  useEffect(() => {
    if (!marketplace) fetchMarketplaceDetails();
  }, [apiAprilBaseUri, apiBaseUri, fetchMarketplaceDetails, marketplace]);

  const contextValues = useMemo(
    () => ({
      marketplaceId,
      marketplace,
      setMarketplace,
      fetchMarketplace: fetchMarketplaceDetails,
    }),
    [fetchMarketplaceDetails, marketplace, marketplaceId],
  );
  console.log(manage);
  return (
    <Wrapper>
      <CreateMarketplaceContext.Provider value={contextValues}>
        <NavBackLink>
          <NavLink className="link-btn nav-link" to="/admin-dashboard/marketplaces">
            &lt; Back to marketplaces
          </NavLink>
        </NavBackLink>
        {isLoading && (
          <SpinnerWrapper>
            <Spinner color="#0016D1" />
          </SpinnerWrapper>
        )}
        {!isLoading && errorMsg.length > 0 && (
          <Message className="mt-2 mb-0" description={errorMsg} success={false} handleClose={handleCloseMsg} />
        )}
        {!isLoading && (
          <>
            {marketplace && (
              <>
                <MarketplaceBasicInfo />
                <LpBox>
                  <Tabs
                    tabOptions={[
                      {
                        text: 'Merchants',
                        value: 'merchants',
                        uri: `${baseUri}/marketplaces/${marketplaceId}/merchants`,
                        isActive: manage === 'merchants',
                      },
                      {
                        text: 'General settings',
                        value: 'general-settings',
                        uri: `${baseUri}/marketplaces/${marketplaceId}/general-settings`,
                        isActive: manage === 'general-settings',
                      },
                      {
                        text: 'Merchant settings',
                        value: 'merchant-settings',
                        uri: `${baseUri}/marketplaces/${marketplaceId}/merchant-settings`,
                        isActive: manage === 'merchant-settings',
                      },
                      {
                        text: 'Users',
                        value: 'users',
                        uri: `${baseUri}/marketplaces/${marketplaceId}/users`,
                        isActive: manage === 'users',
                      },
                    ]}
                    testId={'marketplace-menu'}
                  >
                    <TabContent>
                      <MerchantList />
                    </TabContent>
                    <TabContent>
                      <MarketplaceGeneralSettings />
                    </TabContent>
                    <TabContent>
                      <MerchantMarketplaceSettings />
                    </TabContent>
                    <TabContent>
                      <MarketplaceUsers />
                    </TabContent>
                  </Tabs>
                </LpBox>
              </>
            )}
          </>
        )}
      </CreateMarketplaceContext.Provider>
    </Wrapper>
  );
};

export default ManageMarketplace;
